/**
 * InsightAnalysisSelect — the ONE insight analysis a strategy is built on.
 *
 * Replaces `InsightAnalysisRadioList` (owner decision 2026-09-03, design brief
 * «strategy-intake-feasibility»): a flat list of radio cards grew with every
 * analysis and pushed the rest of the create dialog below the fold. One
 * outlined field now carries the pinned analysis and its facts; the options
 * list opens on demand.
 *
 * The pin is STATE, never a selection path: the component emits the analysis
 * id, the task ids it spans (so downstream pickers can cross-filter) and the
 * accepted insight-variable count the caller already knows about.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { InsightsFirestoreService } from '@modules/insights-analyzer/services/InsightsFirestoreService';
import type { InsightsAnalysisDoc } from '@modules/insights-analyzer/services/InsightsFirestoreService';

/**
 * What the caller needs to tell a HYDRATING screen from a finished one
 * (W12 Task B9d): while `loading` is true — or the list has arrived with
 * analyses in it and the auto-pin has not fired yet — a surface that shows a
 * readiness verdict next to a launch primary must say «Checking…», not count
 * blockers against data that has not arrived (designer invariant 5). A project
 * with `empty: true` is NOT hydrating: it gets the real verdict.
 */
export interface InsightAnalysisHydration {
  loading: boolean;
  /** The list is loaded and carries no completed analysis at all. */
  empty: boolean;
}

export interface InsightAnalysisSelection {
  analysisId: string;
  /** Human label of the pinned analysis (name, else lineage label, else id). */
  analysisName: string;
  /** Task ids the analysis spans (perTaskPackages keys; [] for legacy docs). */
  taskIds: string[];
  /** Accepted insight-variable count shown on the option chip (0 when unknown). */
  acceptedVariableCount: number;
  /**
   * W13 (IS1): the GLOBAL package's theses, id + category. The dialog measures
   * the operator's thesis selection against them BEFORE submit, so a selection
   * that starves the global scope is refused in the readiness strip instead of
   * ~40 s later inside a failed task. Empty for an analysis whose doc-level
   * `aggregatedInsights` is not inlined (the scoped `category_packages` shape):
   * the check then stays inert and the server has the last word.
   */
  globalInsights: Array<{ id: string; category: string }>;
}

export interface InsightAnalysisSelectProps {
  projectId: string;
  /** Currently pinned analysis id (first-class dialog state, not a path). */
  value: string | null;
  onChange: (selection: InsightAnalysisSelection) => void;
  /** Optional per-analysis accepted insight-variable counts (rendered as a chip). */
  acceptedVariableCounts?: Record<string, number>;
  /**
   * Quick create (W12 Part B, Task B7): pin the LATEST completed analysis — the
   * first of the createdAt-desc list this component owns — as soon as the list
   * is loaded and nothing is pinned yet. The pin travels through the SAME
   * `onChange` an operator pick travels on, so the caller's pin handler and
   * every default that follows it are never bypassed. Fires at most once, and
   * never over a pin the caller already carries. The guided wizard leaves it
   * off: it always opens unpinned (owner ruling 2026-09-10).
   */
  autoPinLatest?: boolean;
  /**
   * Reports the list's own hydration state to the caller. Memoise the handler:
   * it is called from an effect whenever `loading` or emptiness changes.
   */
  onHydrationChange?: (state: InsightAnalysisHydration) => void;
  disabled?: boolean;
  'data-testid'?: string;
}

export function deriveAnalysisTaskIds(doc: InsightsAnalysisDoc | undefined): string[] {
  if (!doc) return [];
  if (doc.perTaskPackages && typeof doc.perTaskPackages === 'object') {
    return Object.keys(doc.perTaskPackages);
  }
  const taskIds = new Set<string>();
  for (const items of Object.values(doc.aggregatedInsights ?? {})) {
    if (!Array.isArray(items)) continue;
    for (const item of items) {
      for (const source of (item?.sources ?? [])) {
        if (source?.taskId) taskIds.add(source.taskId);
      }
    }
  }
  return Array.from(taskIds);
}

/** W13 (IS1): the global package's thesis ids and their categories, when the doc inlines them. */
export function deriveAnalysisGlobalInsights(
  doc: InsightsAnalysisDoc | undefined,
): Array<{ id: string; category: string }> {
  if (!doc) return [];
  const insights: Array<{ id: string; category: string }> = [];
  for (const [category, items] of Object.entries(doc.aggregatedInsights ?? {})) {
    if (!Array.isArray(items)) continue;
    for (const item of items) {
      const id = typeof item?.id === 'string' ? item.id : '';
      if (id) insights.push({ id, category: typeof item?.category === 'string' ? item.category : category });
    }
  }
  return insights;
}

/** Human label: the analysis name, its lineage label, or the raw id as last resort. */
function analysisLabel(doc: InsightsAnalysisDoc): string {
  return doc.name || doc.versionLabel || doc.id;
}

/** «18 Jun 2026» — stable across locales; empty when the doc carries no date. */
function formatCompletedDate(doc: InsightsAnalysisDoc): string {
  const raw = doc.updatedAt || doc.createdAt;
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function acceptedChipLabel(count: number): string {
  return count > 0 ? `${count} accepted variables` : '0 accepted';
}

/**
 * «3 tasks · 18 actors · completed 18 Jun 2026» — the facts that pick an
 * analysis. The collapsed control also folds the accepted-variable count in
 * (the option rows carry it as a chip instead).
 */
function factsLine(doc: InsightsAnalysisDoc, acceptedCount?: number): string {
  const taskCount = deriveAnalysisTaskIds(doc).length;
  const actorCount = doc.selectedActorRefs?.length ?? 0;
  const completed = formatCompletedDate(doc);
  const parts = [
    taskCount ? `${taskCount} task${taskCount === 1 ? '' : 's'}` : null,
    actorCount ? `${actorCount} actor${actorCount === 1 ? '' : 's'}` : null,
    acceptedCount !== undefined ? acceptedChipLabel(acceptedCount) : null,
    completed ? `completed ${completed}` : null,
  ].filter(Boolean);
  return parts.join(' · ') || doc.id;
}

const EMPTY_HELPER =
  'No completed insight analyses yet. Run an insights analysis first — generation grounds on it.';

export const InsightAnalysisSelect: React.FC<InsightAnalysisSelectProps> = ({
  projectId,
  value,
  onChange,
  acceptedVariableCounts,
  autoPinLatest = false,
  onHydrationChange,
  disabled = false,
  'data-testid': dataTestId,
}) => {
  const [analyses, setAnalyses] = useState<InsightsAnalysisDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const labelId = `insight-analysis-select-label-${projectId}`;

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const service = new InsightsFirestoreService(projectId);
        // createdAt-desc comes from the service query; keep that order.
        const docs = await service.list({ limit: null });
        if (isMounted) {
          setAnalyses(docs.filter((doc) => doc.status === 'completed'));
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load insight analyses');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    void load();
    return () => { isMounted = false; };
  }, [projectId]);

  /**
   * A pinned analysis that is not (or no longer) in the loaded list is seeded as
   * an option: an out-of-range MUI Select value renders a BLANK control, which
   * would silently hide the pin the dialog is about to submit.
   */
  const options = useMemo<InsightsAnalysisDoc[]>(() => {
    if (!value || analyses.some((doc) => doc.id === value)) return analyses;
    return [...analyses, { id: value } as InsightsAnalysisDoc];
  }, [analyses, value]);

  const byId = useMemo(
    () => new Map(options.map((doc) => [doc.id, doc])),
    [options],
  );

  /** The ONE place a selection is built — the operator's pick and the quick-create auto-pin share it. */
  const emitSelection = useCallback((analysisId: string) => {
    const doc = byId.get(analysisId);
    onChange({
      analysisId,
      analysisName: doc ? analysisLabel(doc) : analysisId,
      taskIds: deriveAnalysisTaskIds(doc),
      acceptedVariableCount: acceptedVariableCounts?.[analysisId] ?? 0,
      globalInsights: deriveAnalysisGlobalInsights(doc),
    });
  }, [byId, onChange, acceptedVariableCounts]);

  const handleChange = useCallback((event: SelectChangeEvent<string>) => {
    const analysisId = event.target.value;
    if (!analysisId) return;
    emitSelection(analysisId);
  }, [emitSelection]);

  /**
   * Task B7: quick create opens on the latest completed analysis. Guarded by a
   * ref rather than by `value` alone, so an operator who picks a DIFFERENT
   * analysis is never dragged back to the newest one by a re-render.
   */
  const autoPinnedRef = useRef(false);
  useEffect(() => {
    if (!autoPinLatest || value || loading || autoPinnedRef.current) return;
    if (analyses.length === 0) return;
    autoPinnedRef.current = true;
    emitSelection(analyses[0].id);
  }, [autoPinLatest, value, loading, analyses, emitSelection]);

  // The list owns `loading`; the caller owns what a hydrating screen shows.
  useEffect(() => {
    onHydrationChange?.({ loading, empty: !loading && options.length === 0 });
  }, [loading, options.length, onHydrationChange]);

  const helperText = error
    ?? (loading ? 'Loading insight analyses…' : (options.length === 0 ? EMPTY_HELPER : null));

  const renderSelectedValue = useCallback((selected: string) => {
    const doc = byId.get(selected);
    if (!doc) return selected;
    const acceptedCount = acceptedVariableCounts ? (acceptedVariableCounts[doc.id] ?? 0) : undefined;
    const facts = factsLine(doc, acceptedCount);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Typography variant="body2" noWrap>{analysisLabel(doc)}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap>{facts}</Typography>
      </Box>
    );
  }, [byId, acceptedVariableCounts]);

  return (
    <FormControl
      fullWidth
      error={Boolean(error)}
      disabled={disabled || loading || options.length === 0}
      data-testid={dataTestId}
    >
      <InputLabel id={labelId} shrink={Boolean(value)}>Insight analysis *</InputLabel>
      <Select
        labelId={labelId}
        label="Insight analysis *"
        value={value ?? ''}
        onChange={handleChange}
        renderValue={renderSelectedValue}
        MenuProps={{ slotProps: { paper: { sx: { maxHeight: 360 } } } }}
      >
        {options.map((doc) => {
          // The map's presence — not a per-id entry — decides the chip, so an
          // analysis with no accepted variables reads «0 accepted», never blank.
          const acceptedCount = acceptedVariableCounts ? (acceptedVariableCounts[doc.id] ?? 0) : undefined;
          return (
            <MenuItem
              key={doc.id}
              value={doc.id}
              data-testid={`strategy-insight-analysis-item-${doc.id}`}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
            >
              <Typography variant="body2" sx={{ flexShrink: 0 }}>
                {analysisLabel(doc)}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flex: 1, minWidth: 0 }}
                noWrap
              >
                {factsLine(doc)}
              </Typography>
              {acceptedCount !== undefined && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={acceptedChipLabel(acceptedCount)}
                  sx={{ flexShrink: 0 }}
                />
              )}
            </MenuItem>
          );
        })}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};
